import type { CrossroadsLog } from '@/api/hooks/useIntegrationLogs'
import {
  type SPRow,
  type SupplyPlanFilters,
  fetchSupplyPlanBulkDownload,
  fetchSupplyPlanFilterValues,
  fetchSupplyPlans,
} from '@/api/hooks/useSupplyPlans'
import { useServerSideDataSource } from '@/api/useServerSideDatasource/useServerSideDatasource'
import { ControlBarSearch } from '@/components/Display/ControlBarSearch'
import { EnvModeSwitch } from '@/components/Display/EnvModeSwitch'
import { ExcelDownloadButton } from '@/components/UploadDownload/ExcelDownloadButton'
import { LogDetailsSidebar } from '@/pages/Dashboard/panels/ConnectionLogsPanel/components/LogDetailsSidebar'
import { useDebouncedSearch } from '@/pages/Dashboard/panels/ReferenceDataPanel/hooks/useDebouncedSearch'
import { SupplyPlanAuditModal } from '@/pages/SupplyPlans/components/SupplyPlanAuditModal'
import { useTenantStore } from '@/stores/tenantStore'
import { ALL_MODES, type EnvModeSelection, toModeParam } from '@/utils/envMode'
import { ClearOutlined } from '@ant-design/icons'
import {
  GraviButton,
  GraviGrid,
  Horizontal,
  Texto,
  Vertical,
} from '@gravitate-js/excalibrr'
import { useQueryClient } from '@tanstack/react-query'
import type {
  CellClickedEvent,
  CellKeyDownEvent,
  FilterChangedEvent,
  FirstDataRenderedEvent,
  GetRowIdParams,
  GridApi,
  ICellRendererParams,
  NavigateToNextCellParams,
} from 'ag-grid-community'
import { DatePicker, message } from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import {
  type MutableRefObject,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  type GetFilterOptions,
  UNKNOWN_GROUP_LABEL,
  createSupplyPlansColumnDefs,
} from './supplyPlansColumnDefs'

const PAGE_SIZE = 100
const STORAGE_KEY = 'supplyplan-grid-v4'

function GroupInnerRenderer({ node, data }: ICellRendererParams<SPRow>) {
  if (!node.group) return null
  const childCount =
    data && typeof data === 'object' && 'childCount' in data
      ? (data as { childCount: number }).childCount
      : undefined
  return (
    <Horizontal flex={1} alignItems='center'>
      <Texto>
        <b>
          {node.key || UNKNOWN_GROUP_LABEL}
          {childCount != null ? ` (${childCount})` : ''}
        </b>
      </Texto>
    </Horizontal>
  )
}

function getRowId(params: GetRowIdParams<SPRow>) {
  if (params.data?.id) return params.data.id

  const groupKey = Object.entries(params.data ?? {})
    .filter(([field]) => field !== 'childCount')
    .map(([, value]) => String(value))
    .join('|')
  return `group-${params.level}-${params.parentKeys?.join('|') ?? ''}-${groupKey}`
}

export function SupplyPlansPage() {
  const selectedTenantName = useTenantStore().selectedTenant?.name
  const [selectedLog, setSelectedLog] = useState<CrossroadsLog | null>(null)
  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null)
  const [auditSupplyPlanId, setAuditSupplyPlanId] = useState<string | null>(
    null,
  )
  const [asOf, setAsOf] = useState<Dayjs>(() => dayjs())

  const gridApiRef = useRef() as MutableRefObject<GridApi>
  const [hasActiveFilters, setHasActiveFilters] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)

  const [searchParams, setSearchParams] = useSearchParams()
  const batchId = searchParams.get('batch_id') ?? undefined

  // The batch deep link is one of the filters, so clearing the filters drops it
  // too -- leaving it in the URL would put it back on the next page load.
  const clearFilters = useCallback(() => {
    gridApiRef.current?.setFilterModel(null)
    setSearchParams(
      (params) => {
        params.delete('batch_id')
        return params
      },
      { replace: true },
    )
  }, [setSearchParams])

  // Arriving on a specific batch means the batch is the thing being looked at,
  // and it may well have synced through a Test instance -- defaulting to Prod
  // would show an empty grid rather than the batch the link points at. Same
  // rule as the Supply Plan logs panel this page is linked from.
  const [mode, setMode] = useState<EnvModeSelection>(() =>
    batchId ? ALL_MODES : 'Prod',
  )

  const clearedStaleFilterRef = useRef(false)
  if (batchId && !clearedStaleFilterRef.current) {
    clearedStaleFilterRef.current = true
    try {
      const key = `gridConfig::${STORAGE_KEY}`
      const raw = localStorage.getItem(key)
      const parsed = raw ? JSON.parse(raw) : null
      if (parsed && 'filter' in parsed) {
        delete parsed.filter
        localStorage.setItem(key, JSON.stringify(parsed))
      }
    } catch {
      // Malformed/missing entry -- nothing to clear.
    }
  }

  const { searchTerm, setSearchTerm } = useDebouncedSearch()

  const filters = useMemo<SupplyPlanFilters>(
    () => ({
      asOf: asOf.format('YYYY-MM-DD'),
      mode: toModeParam(mode),
      tenantName: selectedTenantName,
    }),
    [asOf, mode, selectedTenantName],
  )

  const { serverSideDatasource, isFetching, rowCount } =
    useServerSideDataSource(filters, searchTerm, fetchSupplyPlans)

  const handleDownload = useCallback(async () => {
    setIsDownloading(true)
    try {
      const blob = await fetchSupplyPlanBulkDownload({
        filters,
        filterModel: gridApiRef.current?.getFilterModel(),
      })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'Supply Plans.xlsx')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Supply plan download failed:', error)
      message.error('Download failed. Please try again.')
    } finally {
      setIsDownloading(false)
    }
  }, [filters])

  const queryClient = useQueryClient()
  const getFilterOptions = useCallback<GetFilterOptions>(
    (colId) =>
      queryClient.fetchQuery({
        queryKey: [
          'supply-plan-filter-values',
          colId,
          selectedTenantName,
          filters,
        ],
        queryFn: () => fetchSupplyPlanFilterValues(colId, filters),
        // Absorbs open/close/reopen without refetching; a tenant or As Of
        // change lands on a different key and fetches regardless.
        staleTime: 60_000,
      }),
    [queryClient, selectedTenantName, filters],
  )

  const columnDefs = useMemo(
    () =>
      createSupplyPlansColumnDefs({
        tenantName: selectedTenantName ?? '',
        onAuditClick: (row) => setAuditSupplyPlanId(row.supply_plan_id),
      }),
    [selectedTenantName],
  )

  const openLogRowIdRef = useRef<string | null>(null)

  const openRow = useCallback((row: SPRow | undefined) => {
    if (row?.log) {
      setSelectedLog(row.log)
      openLogRowIdRef.current = row.id
    }
    setSelectedBatchId(row?.batch_id ?? null)
  }, [])

  const closeRow = useCallback(() => {
    setSelectedLog(null)
    setSelectedBatchId(null)
    openLogRowIdRef.current = null
  }, [])

  const agPropOverrides = useMemo(
    () => ({
      rowModelType: 'serverSide' as const,
      serverSideDatasource,
      pagination: true,
      paginationPageSize: PAGE_SIZE,
      cacheBlockSize: PAGE_SIZE,
      getRowId,
      context: { getFilterOptions },
      rowGroupPanelShow: 'always' as const,
      groupRowRendererParams: {
        innerRenderer: GroupInnerRenderer,
        suppressPadding: true,
        suppressCount: true,
      },
      rowSelection: 'single' as const,
      onCellClicked: (event: CellClickedEvent<SPRow>) => openRow(event.data),
      onCellKeyDown: (event: CellKeyDownEvent<SPRow>) => {
        if ((event.event as KeyboardEvent | null)?.key !== 'Enter') return
        if (event.data?.id === openLogRowIdRef.current) closeRow()
        else openRow(event.data)
      },
      navigateToNextCell: (params: NavigateToNextCellParams<SPRow>) => {
        const next = params.nextCellPosition
        if (next && (params.key === 'ArrowUp' || params.key === 'ArrowDown')) {
          const node = params.api.getDisplayedRowAtIndex(next.rowIndex)
          node?.setSelected(true)
          if (openLogRowIdRef.current !== null) openRow(node?.data)
        }
        return next
      },
      onFilterChanged: (event: FilterChangedEvent<SPRow>) =>
        setHasActiveFilters(event.api.isAnyFilterPresent()),
      onFirstDataRendered: (event: FirstDataRenderedEvent<SPRow>) => {
        if (batchId) {
          event.api.setFilterModel({
            batch_id: { filterType: 'text', type: 'equals', filter: batchId },
          })
        }
      },
    }),
    [serverSideDatasource, getFilterOptions, batchId, openRow, closeRow],
  )

  return (
    <Vertical className='h-full p-3'>
      <Horizontal gap={16} flex={1} style={{ minHeight: 0 }}>
        <Vertical flex={2.5}>
          <GraviGrid<SPRow, Record<string, unknown>>
            columnDefs={columnDefs}
            agPropOverrides={agPropOverrides}
            loading={isFetching}
            externalRef={gridApiRef}
            fullWidthSearchBar
            controlBarProps={{
              rowCount,
              title: 'Supply Plans',
              customSearchBar: (
                <ControlBarSearch
                  onChange={setSearchTerm}
                  placeholder='Search sites, products'
                />
              ),
              actionButtons: (
                <Horizontal gap={8} alignItems='center'>
                  {hasActiveFilters && (
                    <GraviButton
                      size='middle'
                      appearance='outline'
                      color='blue'
                      icon={<ClearOutlined />}
                      onClick={clearFilters}
                    >
                      Clear Filters
                    </GraviButton>
                  )}
                  <EnvModeSwitch value={mode} onChange={setMode} />
                  <Texto appearance='hint'>As Of</Texto>
                  <DatePicker
                    value={asOf}
                    onChange={(date) => date && setAsOf(date)}
                    format='YYYY-MM-DD'
                    allowClear={false}
                  />
                  <ExcelDownloadButton
                    onDownload={handleDownload}
                    isLoading={isDownloading}
                  />
                </Horizontal>
              ),
            }}
            storageKey={STORAGE_KEY}
          />
        </Vertical>
        <LogDetailsSidebar
          selectedLog={selectedLog}
          groupMetadata={null}
          selectedTenantName={selectedTenantName}
          // No retry support yet for supply plans (see logGroupId below), so
          // the sidebar's Retry button never renders here — refetchLogs is
          // only reachable from that path.
          logGroupId={null}
          refetchLogs={async () => {
            // no-op — retry isn't wired up for supply plans yet
          }}
          integrationLogsBatchId={selectedBatchId}
          onClose={closeRow}
        />
      </Horizontal>
      <SupplyPlanAuditModal
        supplyPlanId={auditSupplyPlanId}
        open={auditSupplyPlanId !== null}
        onClose={() => setAuditSupplyPlanId(null)}
      />
    </Vertical>
  )
}
